import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles as SparkIcon } from "lucide-react";
import { useAgeGate } from "@/context/AgeGateContext";

/**
 * The big reveal:
 *   1. "Meet" types in, sexy & smooth, letter by letter.
 *   2. "Layla." types in italic hot pink with shimmer.
 *   3. A bright hot-pink flash builds, peaks brilliantly, then fades.
 *   4. The portrait of Layla slowly reveals from behind the flash with a
 *      pulsing neon glow border.
 *
 * Triggers:
 *   - First-time visitor: starts the moment the age gate is confirmed.
 *   - Returning visitor: starts ~250ms after the page mounts (gate skipped).
 *
 * The "verified" flag from AgeGateContext flips to true on confirm, which
 * fires the reveal sequence.
 */

const MEET_TEXT = "Meet";
const NAME_TEXT = "Layla.";

function useTypewriter(text, { startDelay = 0, charDelay = 90, enabled = true } = {}) {
  const [out, setOut] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    setOut("");
    setDone(false);
    let i = 0;
    let cancelled = false;
    const start = setTimeout(function tick() {
      if (cancelled) return;
      if (i <= text.length) {
        setOut(text.slice(0, i));
        i += 1;
        if (i <= text.length) setTimeout(tick, charDelay);
        else setDone(true);
      }
    }, startDelay);
    return () => {
      cancelled = true;
      clearTimeout(start);
    };
  }, [text, startDelay, charDelay, enabled]);
  return { out, done };
}

export default function HeroReveal({ heroImg, name = "Layla", subtitle }) {
  const { verified } = useAgeGate();
  const [phase, setPhase] = useState("idle"); // idle → typing → flash → reveal → settled
  const [armed, setArmed] = useState(false);

  // Arm the sequence the moment the gate is verified (or on mount if already
  // verified). NOTE: we deliberately don't use a cleanup-cancellable timer
  // here — React StrictMode double-mounts effects which would cancel the
  // timer and leave the hero stuck in the "idle" state for returning visitors.
  useEffect(() => {
    if (!verified) return;
    if (armed) return;
    setArmed(true);
  }, [verified, armed]);

  // Phase: typing
  const meet = useTypewriter(MEET_TEXT, { startDelay: 150, charDelay: 95, enabled: armed });
  const lala = useTypewriter(NAME_TEXT, {
    startDelay: 150 + MEET_TEXT.length * 95 + 250,
    charDelay: 130,
    enabled: armed,
  });

  // After the name finishes typing → flash → reveal → settle
  useEffect(() => {
    if (!armed) return;
    if (!lala.done) {
      setPhase("typing");
      return;
    }
    setPhase("flash");
    const t1 = setTimeout(() => setPhase("reveal"), 900);
    const t2 = setTimeout(() => setPhase("settled"), 2400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [armed, lala.done]);

  const showImage = phase === "reveal" || phase === "settled";

  return (
    <div className="relative max-w-6xl mx-auto w-full px-6 sm:px-10 lg:px-16 flex flex-col items-center text-center">
      {/* Top eyebrow */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: armed ? 1 : 0, y: 0 }}
        transition={{ duration: 0.7 }}
        className="flex items-center gap-3 mb-6"
      >
        <span className="w-10 h-px bg-pink-hot" />
        <span className="text-[10px] tracking-[0.35em] uppercase text-pink-blush">
          Private · Discreet · Unforgettable
        </span>
        <span className="w-10 h-px bg-pink-hot" />
      </motion.div>

      {/* The big typed heading */}
      <h1
        className="font-display text-6xl sm:text-7xl lg:text-8xl xl:text-[8rem] leading-[0.9] text-champagne mb-3 select-none"
        data-testid="hero-heading"
        aria-label={`Meet ${name}`}
      >
        <span className="inline-block">
          {meet.out}
          {phase === "typing" && !meet.done && (
            <span
              className="inline-block w-[3px] align-baseline ml-1 animate-pulse"
              style={{ height: "0.85em", background: "#FF1493" }}
            />
          )}
        </span>{" "}
        <span
          className="italic text-pink-hot"
          style={{
            backgroundImage:
              "linear-gradient(90deg, #FF1493 0%, #FFB5D3 25%, #FFFFFF 50%, #FFB5D3 75%, #FF1493 100%)",
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            animation: "shimmer 5s linear infinite",
            filter: "drop-shadow(0 0 22px rgba(255,20,147,0.65))",
          }}
        >
          {meet.done ? lala.out : ""}
          {phase === "typing" && meet.done && !lala.done && (
            <span
              className="inline-block w-[3px] align-baseline ml-1 animate-pulse"
              style={{ height: "0.85em", background: "#FF1493" }}
            />
          )}
        </span>
      </h1>

      {/* Tag line — appears AFTER the typing to keep focus on the reveal */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: phase === "settled" ? 1 : 0, y: phase === "settled" ? 0 : 10 }}
        transition={{ duration: 0.9, delay: 0.05 }}
        className="max-w-2xl text-lg sm:text-xl text-champagne/80 leading-relaxed font-display italic mb-10"
      >
        Let me help you relax and take the edge off after a long day — and lift the heavy load right off your shoulders.
      </motion.p>

      {/* Portrait stage */}
      <div className="relative w-full max-w-md mx-auto mb-10" style={{ minHeight: 480 }}>
        {/* Bright pink flash — builds, peaks, fades */}
        <AnimatePresence>
          {(phase === "flash" || phase === "reveal") && (
            <motion.div
              key="flash"
              aria-hidden
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{
                opacity: phase === "flash" ? [0, 0.55, 1, 0.85] : [0.85, 0.4, 0],
                scale: phase === "flash" ? [0.4, 0.8, 1.15, 1.4] : [1.4, 1.7, 2.0],
              }}
              exit={{ opacity: 0, scale: 2.2 }}
              transition={{
                duration: phase === "flash" ? 1.0 : 1.5,
                ease: "easeOut",
                times: phase === "flash" ? [0, 0.3, 0.65, 1] : [0, 0.5, 1],
              }}
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at center, rgba(255,255,255,0.9) 0%, rgba(255,20,147,0.95) 18%, rgba(255,20,147,0.55) 38%, rgba(255,20,147,0.15) 60%, transparent 75%)",
                filter: "blur(14px)",
                mixBlendMode: "screen",
                zIndex: 4,
              }}
            />
          )}
        </AnimatePresence>

        {/* Persistent neon glow halo around the frame after reveal */}
        <motion.div
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: showImage ? 1 : 0 }}
          transition={{ duration: 1.2, delay: showImage ? 0.2 : 0 }}
          className="absolute -inset-3 rounded-lg pointer-events-none"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,20,147,0.55), rgba(255,181,211,0.25) 45%, rgba(229,178,153,0.35))",
            filter: "blur(28px)",
            zIndex: 1,
          }}
        />
        <motion.div
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: showImage ? 1 : 0 }}
          transition={{ duration: 1.5, delay: showImage ? 0.3 : 0 }}
          className="absolute -inset-1 rounded-md pointer-events-none animate-pink-pulse"
          style={{
            boxShadow:
              "0 0 60px 8px rgba(255,20,147,0.55), inset 0 0 40px rgba(255,20,147,0.25)",
            zIndex: 2,
          }}
        />

        {/* The portrait itself — slow, big-reveal scale + opacity + clip */}
        <motion.div
          initial={{ opacity: 0, scale: 1.18, filter: "blur(24px) brightness(2.2)" }}
          animate={
            showImage
              ? { opacity: 1, scale: 1, filter: "blur(0px) brightness(1)" }
              : { opacity: 0, scale: 1.18, filter: "blur(24px) brightness(2.2)" }
          }
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-md border border-pink-hot/40"
          style={{ zIndex: 3 }}
        >
          {heroImg ? (
            <img
              src={heroImg}
              alt={name}
              className="w-full aspect-[3/4] object-cover"
              data-testid="hero-portrait"
            />
          ) : (
            <div className="w-full aspect-[3/4] bg-gradient-to-b from-noir-surface to-noir-elevated flex items-center justify-center">
              <span className="font-display italic text-pink-hot text-5xl">{name}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-noir-base/40 via-transparent to-transparent pointer-events-none" />
          {/* Soft shimmer sweep across the portrait on reveal */}
          <AnimatePresence>
            {phase === "reveal" && (
              <motion.div
                key="sheen"
                aria-hidden
                initial={{ x: "-120%", opacity: 0.0 }}
                animate={{ x: "120%", opacity: 0.7 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.4, ease: "easeOut" }}
                className="absolute inset-y-0 -inset-x-1/3 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%)",
                  mixBlendMode: "screen",
                }}
              />
            )}
          </AnimatePresence>
        </motion.div>

        {/* Floating "Available Tonight" tag */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: phase === "settled" ? 1 : 0, y: phase === "settled" ? 0 : 10 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="absolute -bottom-4 left-1/2 -translate-x-1/2 glass-panel px-5 py-2 rounded-sm flex items-center gap-2 whitespace-nowrap"
          style={{ zIndex: 5 }}
        >
          <SparkIcon className="w-3.5 h-3.5 text-pink-hot" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-champagne/90">
            Available Tonight
          </span>
        </motion.div>
      </div>

      {/* Subtitle below portrait — reveals when settled */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: phase === "settled" ? 1 : 0, y: phase === "settled" ? 0 : 10 }}
        transition={{ duration: 0.9, delay: 0.35 }}
        className="max-w-2xl text-base sm:text-[17px] text-champagne/75 leading-relaxed mt-6 mb-10"
      >
        {subtitle ||
          "Easy-going, bubbly, and unforgettable — the best there ever was, and the best there ever will be."}
      </motion.p>
    </div>
  );
}
